/**
 * Agentic AI API Client
 * Frontend API functions for Agentic AI features
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const agenticApi = {
  // Get Agentic AI status
  getStatus: (headers) => 
    axios.get(`${API_URL}/api/agentic/status`, { headers }),

  // Document Advisor
  getDocumentInsights: (docId, language, headers) =>
    axios.get(`${API_URL}/api/agentic/documents/${docId}/insights`, { 
      headers,
      params: { language }
    }),

  // Risk Monitor
  getRiskAnalysis: (timeWindowHours, headers) =>
    axios.get(`${API_URL}/api/agentic/security/risk-analysis`, {
      headers,
      params: { time_window_hours: timeWindowHours }
    }),

  getSecurityAlerts: (headers) =>
    axios.get(`${API_URL}/api/agentic/security/alerts`, { headers }),

  // Workflow Builder
  generateWorkflow: (description, documentTypes, language, headers) =>
    axios.post(`${API_URL}/api/agentic/workflows/generate`, {
      description,
      document_types: documentTypes,
      language
    }, { headers }),

  // Redactor
  detectSensitiveData: (text, categories, minSeverity, headers) =>
    axios.post(`${API_URL}/api/agentic/redact/detect`, {
      text,
      categories,
      min_severity: minSeverity
    }, { headers }),

  getDocumentRedactionSuggestions: (docId, headers) =>
    axios.get(`${API_URL}/api/agentic/documents/${docId}/redaction-suggestions`, { headers }),

  // Lifecycle Predictor
  getDocumentLifecycle: (docId, language, headers) =>
    axios.get(`${API_URL}/api/agentic/documents/${docId}/lifecycle`, {
      headers,
      params: { language }
    }),

  getExpiringDocuments: (daysAhead, headers) =>
    axios.get(`${API_URL}/api/agentic/documents/expiring`, {
      headers,
      params: { days_ahead: daysAhead }
    }),

  // Knowledge Graph
  getDocumentGraphContext: (docId, headers) =>
    axios.get(`${API_URL}/api/agentic/graph/document/${docId}/context`, { headers }),

  getGraphStats: (headers) =>
    axios.get(`${API_URL}/api/agentic/graph/stats`, { headers }),

  reindexKnowledgeGraph: (headers) =>
    axios.post(`${API_URL}/api/agentic/graph/reindex`, {}, { headers }),

  getUserActivityGraph: (userId, headers) =>
    axios.get(`${API_URL}/api/agentic/graph/user/${userId}/activity`, { headers }),

  // Intelligence Dashboard
  getIntelligenceSummary: (timeRangeDays, headers) =>
    axios.get(`${API_URL}/api/agentic/intelligence/summary`, {
      headers,
      params: { time_range_days: timeRangeDays }
    }),

  getQuickStats: (headers) =>
    axios.get(`${API_URL}/api/agentic/intelligence/quick-stats`, { headers }),

  // Voice Agent
  transcribeAudio: (formData, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/transcribe`, formData, { 
      headers: { ...headers, 'Content-Type': 'multipart/form-data' }
    }),

  processVoiceQuery: (formData, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/query`, formData, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' }
    }),

  parseVoiceCommand: (data, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/command`, data, { headers }),

  executeVoiceCommand: (formData, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/execute`, formData, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' }
    }),

  // Conversational Workflow Builder
  startWorkflowChat: (headers) =>
    axios.get(`${API_URL}/api/agentic/workflows/chat/start`, { headers }),

  chatWorkflow: (data, headers) =>
    axios.post(`${API_URL}/api/agentic/workflows/chat`, data, { headers }),

  // Cross-Lingual Search
  translateSearchQuery: (data, headers) =>
    axios.post(`${API_URL}/api/agentic/search/translate`, data, { headers }),

  // Notifications
  sendExpiryAlerts: (headers) =>
    axios.post(`${API_URL}/api/agentic/notifications/send-expiry-alerts`, {}, { headers }),

  getNotificationStatus: (headers) =>
    axios.get(`${API_URL}/api/agentic/notifications/status`, { headers }),

  // Custom Voice Commands (Per-Tenant)
  getCustomCommands: (activeOnly, headers) =>
    axios.get(`${API_URL}/api/agentic/voice/custom-commands`, {
      headers,
      params: { active_only: activeOnly }
    }),

  createCustomCommand: (data, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/custom-commands`, data, { headers }),

  getCommandSuggestions: (headers) =>
    axios.get(`${API_URL}/api/agentic/voice/custom-commands/suggestions`, { headers }),

  getCustomCommand: (commandId, headers) =>
    axios.get(`${API_URL}/api/agentic/voice/custom-commands/${commandId}`, { headers }),

  updateCustomCommand: (commandId, data, headers) =>
    axios.put(`${API_URL}/api/agentic/voice/custom-commands/${commandId}`, data, { headers }),

  deleteCustomCommand: (commandId, headers) =>
    axios.delete(`${API_URL}/api/agentic/voice/custom-commands/${commandId}`, { headers }),

  // Text-to-Speech
  textToSpeech: (data, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/tts`, data, { headers }),

  ttsForCommand: (commandType, description, voice, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/tts/command`, null, {
      headers,
      params: { command_type: commandType, description, voice }
    }),

  getTTSVoices: (headers) =>
    axios.get(`${API_URL}/api/agentic/voice/tts/voices`, { headers }),

  // Context-Aware Commands
  recordContextAction: (data, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/context/record`, data, { headers }),

  resolveContextReferences: (text, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/context/resolve`, { text }, { headers }),

  getContextualSuggestions: (headers) =>
    axios.get(`${API_URL}/api/agentic/voice/context/suggestions`, { headers }),

  // Enhanced Voice Command (with context + custom)
  enhancedVoiceCommand: (text, headers) =>
    axios.post(`${API_URL}/api/agentic/voice/command/enhanced`, { text }, { headers }),
};

export default agenticApi;
